/**
 * Invoice arithmetic — a verbatim copy of
 * `backend/src/lib/anafEfactura/invoiceMath.ts` so the live totals in the
 * form are exactly what gets issued. Keep the two in sync. Pure, dependency-free.
 *
 * Rounding follows EN 16931:
 *  - per line: 2 decimals for the line net (`LineExtensionAmount`);
 *  - per VAT category+rate: VAT = round2(Σ taxable × rate) (BR-CO-17), so the
 *    document totals come from the breakdown, not from the sum of line VATs.
 * With "prices include VAT" a multi-line invoice can therefore end a few bani
 * away from the sum of the typed gross prices; a single line is exact.
 */

export type VatCategory = 'S' | 'Z' | 'E' | 'O';

export interface InvoiceLineInput {
  quantity: number;
  /** As typed: gross when `pricesIncludeVat`, net otherwise. */
  unitPrice: number;
  /** Percent, e.g. 21. Ignored (forced to 0) for categories other than S. */
  vatRate: number;
  vatCategory?: VatCategory;
}

export interface InvoiceLineAmounts {
  vatCategory: VatCategory;
  vatRate: number;
  /** Net unit price with 4 decimals (goes to `cac:Price/cbc:PriceAmount`). */
  unitNet: number;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
}

export interface VatBreakdownRow {
  vatCategory: VatCategory;
  vatRate: number;
  taxableAmount: number;
  vatAmount: number;
}

export interface InvoiceTotals {
  lines: InvoiceLineAmounts[];
  breakdown: VatBreakdownRow[];
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
}

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function round4(value: number): number {
  return Math.round((value + Number.EPSILON) * 10_000) / 10_000;
}

function effectiveRate(line: InvoiceLineInput): { category: VatCategory; rate: number } {
  const category = line.vatCategory ?? (line.vatRate > 0 ? 'S' : 'Z');
  if (category !== 'S') return { category, rate: 0 };
  return { category, rate: Number.isFinite(line.vatRate) && line.vatRate > 0 ? line.vatRate : 0 };
}

export function computeLine(line: InvoiceLineInput, pricesIncludeVat: boolean): InvoiceLineAmounts {
  const { category, rate } = effectiveRate(line);
  const qty = Number.isFinite(line.quantity) ? line.quantity : 0;
  const price = Number.isFinite(line.unitPrice) ? line.unitPrice : 0;
  const factor = 1 + rate / 100;
  if (pricesIncludeVat && rate > 0) {
    const grossAmount = round2(qty * price);
    const netAmount = round2(grossAmount / factor);
    return {
      vatCategory: category,
      vatRate: rate,
      unitNet: round4(price / factor),
      netAmount,
      vatAmount: round2(grossAmount - netAmount),
      grossAmount,
    };
  }
  const netAmount = round2(qty * price);
  const vatAmount = round2(netAmount * (rate / 100));
  return {
    vatCategory: category,
    vatRate: rate,
    unitNet: round4(price),
    netAmount,
    vatAmount,
    grossAmount: round2(netAmount + vatAmount),
  };
}

export function computeTotals(lines: InvoiceLineInput[], pricesIncludeVat: boolean): InvoiceTotals {
  const computed = lines.map((l) => computeLine(l, pricesIncludeVat));
  const groups = new Map<string, VatBreakdownRow>();
  for (const l of computed) {
    const key = `${l.vatCategory}:${l.vatRate}`;
    const row = groups.get(key) ?? {
      vatCategory: l.vatCategory,
      vatRate: l.vatRate,
      taxableAmount: 0,
      vatAmount: 0,
    };
    row.taxableAmount = round2(row.taxableAmount + l.netAmount);
    groups.set(key, row);
  }
  const breakdown = [...groups.values()].map((row) => ({
    ...row,
    vatAmount: round2(row.taxableAmount * (row.vatRate / 100)),
  }));
  const netAmount = round2(breakdown.reduce((s, r) => s + r.taxableAmount, 0));
  const vatAmount = round2(breakdown.reduce((s, r) => s + r.vatAmount, 0));
  return {
    lines: computed,
    breakdown,
    netAmount,
    vatAmount,
    totalAmount: round2(netAmount + vatAmount),
  };
}

/** "1234.5" → "1234.50" (UBL amounts always carry two decimals). */
export function money(value: number): string {
  return round2(value).toFixed(2);
}
